// ─────────────────────────────────────────────────────────────────────────────
// AXIS360 SalesAxis — Scoring Engine
// Sales Personality Index (SPI): 16 key items × 3 pts = 0-48
// ─────────────────────────────────────────────────────────────────────────────
import { SALESAXIS_ITEMS, SALESAXIS_SECTIONS } from "./questionBank.js";

export const SPI_BANDS = [
  { key:"avoid",  label:"Avoid",  min:0,  max:18, color:"#C62828", bg:"#FFEBEE",
    hire:"Not recommended for independent sales roles without intensive coaching." },
  { key:"ok",     label:"OK",     min:19, max:23, color:"#E8A020", bg:"#FFF8E1",
    hire:"Entry-level sales with structured onboarding and close management." },
  { key:"good",   label:"Good",   min:24, max:26, color:"#43A047", bg:"#E8F5E9",
    hire:"Recommended. Above-average probability of sales success." },
  { key:"better", label:"Better", min:27, max:33, color:"#1B8040", bg:"#E8F5E9",
    hire:"Strongly recommended. High probability of professional sales success." },
  { key:"best",   label:"Best",   min:34, max:48, color:"#0B5C30", bg:"#E8F5E9",
    hire:"Exceptional. Very high probability of top-half sales performance." },
];

export function getSPIBand(score) {
  return SPI_BANDS.find(b => score >= b.min && score <= b.max) || SPI_BANDS[0];
}

// Map 1-5 Likert to 0-3 SPI points for key items
function likertToSPI(v) {
  if (v >= 5) return 3;
  if (v >= 4) return 2;
  if (v >= 3) return 1;
  return 0;
}

// Build bar score 0-100 for a sub-dimension group
function dimBarScore(items, answers) {
  if (!items.length) return 0;
  const total = items.reduce((s,item) => s + (answers[item.id]||0), 0);
  return Math.round((total / (items.length * 5)) * 100);
}

// Build strength/weakness lists from items
function buildStrengths(items, answers, threshold=4) {
  return items.filter(i => (answers[i.id]||0) >= threshold).map(i => i.subDim);
}
function buildWeaknesses(items, answers, threshold=3) {
  return items.filter(i => (answers[i.id]||0) < threshold).map(i => i.subDim);
}

export function scoreSalesAxis(answers) {
  // answers: { sm01: 1-5, sm02: 1-5, ... }
  const all = SALESAXIS_ITEMS;

  // SPI: sum key items mapped 0-3
  const keyItems = all.filter(i => i.keyItem);
  const spi = keyItems.reduce((s,i) => s + likertToSPI(answers[i.id]||0), 0);
  const band = getSPIBand(spi);

  // Section items
  const pItems = all.filter(i => i.section==="personality");
  const kItems = all.filter(i => i.section==="knowledge");
  const mItems = all.filter(i => i.section==="motivation");

  // Bar data for each section (grouped by subDim)
  const pSubDims = [...new Set(pItems.map(i=>i.subDim))];
  const kSubDims = [...new Set(kItems.map(i=>i.subDim))];
  const mSubDims = [...new Set(mItems.map(i=>i.subDim))];

  function subDimBar(subDim, items, ans) {
    const relevant = items.filter(i=>i.subDim===subDim);
    return { label:subDim, value:dimBarScore(relevant, ans) };
  }

  const personalityBars   = pSubDims.map(s => subDimBar(s, pItems, answers));
  const knowledgeBars     = kSubDims.map(s => subDimBar(s, kItems, answers));
  const motivationBars    = mSubDims.map(s => subDimBar(s, mItems, answers));

  // Strengths & weaknesses
  const personalityStrengths   = buildStrengths(pItems, answers);
  const personalityWeaknesses  = buildWeaknesses(pItems, answers);
  const knowledgeStrengths     = buildStrengths(kItems, answers);
  const knowledgeWeaknesses    = buildWeaknesses(kItems, answers);
  const motivationStrengths    = buildStrengths(mItems, answers);
  const motivationWeaknesses   = buildWeaknesses(mItems, answers);

  // Section averages (0-100)
  const personalityPct = Math.round((pItems.reduce((s,i)=>s+(answers[i.id]||0),0)/(pItems.length*5))*100);
  const knowledgePct   = Math.round((kItems.reduce((s,i)=>s+(answers[i.id]||0),0)/(kItems.length*5))*100);
  const motivationPct  = Math.round((mItems.reduce((s,i)=>s+(answers[i.id]||0),0)/(mItems.length*5))*100);

  return {
    spi, band, spiMax:48,
    personalityBars, knowledgeBars, motivationBars,
    personalityStrengths, personalityWeaknesses,
    knowledgeStrengths, knowledgeWeaknesses,
    motivationStrengths, motivationWeaknesses,
    personalityPct, knowledgePct, motivationPct,
    itemScores: answers,
  };
}
